import { getPfFile, getPfFiles } from '@client-shared/api/pf-files.api'
import ignoreCanceledRequest from '@client-shared/utils/ignore-canceled-request.js'

const fetchAbortControllers = {}

// In-flight single-file fetches keyed by pfFileId. Lets fetchById de-dupe concurrent requests for the same
// file (e.g. a PfFileName label and a PfFilePreview rendered for the same id) by sharing one promise, so
// every caller's `await` resolves only once the file is actually loaded - no premature "not found" flash.
const fetchByIdPromises = {}

export default {
  namespaced: true,

  state: () => {
    return {
      list: [],
      // Project id this store's `list` was last fetched for. Lets the socket
      // reconnect catch-up skip stores that have never been loaded for the
      // current project (see cliesrc/utils/refetch-project-data.js).
      loadedProjectId: null,
      lastFetchedAt: undefined,
      isLoading: false,
      // Ids currently being lazily fetched one-by-one via fetchById. Tracked here (not per-component) so any
      // component referencing the same file can render a loading state via the isFetching getter without
      // passing the state around, and so concurrent fetches of the same id de-dupe (see fetchById).
      fetchingIds: [],
    }
  },

  getters: {
    getById: (state, getters) => {
      // getters.getFilesListMap is a Map with the pfFileId as key and the pfFile as value for fast O(1) access speed
      // getters.getFilesListMap is cached by the store
      // Read here, not inside the returned function: this makes the getter depend on the map, so it
      // returns a new function whenever the pfFiles list changes. The function's identity then
      // tells a cache that pfFiles changed (client-shared/utils/search-index-cache.js)
      const filesListMap = getters.getFilesListMap

      return pfFileId => filesListMap.get(pfFileId)
    },

    getFilesListMap: (state) => {
      return new Map(state.list.map(obj => [
        obj._id,
        obj,
      ]))
    },

    // Reads the map here for the same reason as getById
    getByIds: (state, getters) => {
      const filesListMap = getters.getFilesListMap

      return pfFileIds => {
        return pfFileIds
          .map(pfFileId => filesListMap.get(pfFileId))
      }
    },

    getByS3Key: (state) => {
      return s3Key => s3Key && state.list.find(pfFile => {
        return pfFile.s3Key === s3Key
      })
    },

    isFetching: (state) => {
      return pfFileId => state.fetchingIds.includes(pfFileId)
    },
  },

  mutations: {
    SET_PF_FILES (state, pfFiles) {
      state.list = pfFiles
    },

    SET_LOADED_PROJECT_ID (state, projectId) {
      state.loadedProjectId = projectId
    },

    SET_IS_LOADING (state, isLoading) {
      state.isLoading = isLoading
    },

    SET_LAST_FETCHED_AT (state, lastFetchedAt) {
      state.lastFetchedAt = lastFetchedAt
    },

    ADD_FETCHING_ID (state, pfFileId) {
      if (!state.fetchingIds.includes(pfFileId)) {
        state.fetchingIds.push(pfFileId)
      }
    },

    REMOVE_FETCHING_ID (state, pfFileId) {
      state.fetchingIds = state.fetchingIds.filter(id => id !== pfFileId)
    },

    REPLACE_PF_FILE (state, updatedPfFile) {
      const existingPfFileIndex = state.list.findIndex(pfFile => pfFile._id === updatedPfFile._id)

      if (existingPfFileIndex !== -1) {
        const isUpdateNewer = new Date(updatedPfFile.modified) > new Date(state.list[existingPfFileIndex].modified)

        if (isUpdateNewer) {
          state.list[existingPfFileIndex] = updatedPfFile
        }
      }
    },

    ADD_PF_FILE (state, pfFileToAdd) {
      const existingPfFileIndex = state.list.findIndex(pfFile => pfFile._id === pfFileToAdd._id)

      if (existingPfFileIndex !== -1) {
        const isUpdateNewer = new Date(pfFileToAdd.modified) > new Date(state.list[existingPfFileIndex].modified)

        if (isUpdateNewer) {
          state.list[existingPfFileIndex] = pfFileToAdd
        }
      } else {
        state.list.push(pfFileToAdd)
      }
    },

    FULL_RESET (state) {
      state.list = []
      state.loadedProjectId = null
      state.lastFetchedAt = undefined
      state.isLoading = false
      state.fetchingIds = []
    },
  },

  actions: {
    async fetch ({ commit, rootState }, projectId) {
      if (fetchAbortControllers.fetchList instanceof AbortController) {
        fetchAbortControllers.fetchList.abort()
      }

      const fetchAbortController = new AbortController()

      fetchAbortControllers.fetchList = fetchAbortController

      commit('SET_IS_LOADING', true)

      try {
        const pfFiles = await ignoreCanceledRequest(getPfFiles({
          signal: fetchAbortController.signal,
          axios: this.$axios,
          projectId,
          // from: state.lastFetchedAt, // We switched to load all pfFiles in the webapp. If we only fetch the delta, we have to save the existing files in the localStorage, which exeeds the size limit for bigger projects.
        }))

        if (fetchAbortControllers.fetchList !== fetchAbortController || rootState.project.requestedProjectId !== projectId || !pfFiles) {
          return
        }

        commit('SET_LAST_FETCHED_AT', new Date())

        commit('SET_PF_FILES', pfFiles)
        commit('SET_LOADED_PROJECT_ID', projectId)
      } catch (err) {
        console.error(err)
      } finally {
        if (fetchAbortControllers.fetchList === fetchAbortController) {
          fetchAbortControllers.fetchList = null
          commit('SET_IS_LOADING', false)
        }
      }
    },

    // Fetch a single pfFile by id and add it to the list. Used to lazily pull in a file that isn't part of
    // the loaded list yet (e.g. referenced by a plan/document but missing from the store) without re-fetching
    // the whole list. Tracks the id in fetchingIds (instead of the global isLoading flag) so consumers can show
    // a per-file loading state via the isFetching getter. Concurrent calls for the same id share one in-flight
    // request and all resolve when it finishes (see fetchByIdPromises).
    fetchById ({ commit }, pfFileId) {
      if (fetchByIdPromises[pfFileId]) {
        return fetchByIdPromises[pfFileId]
      }

      commit('ADD_FETCHING_ID', pfFileId)

      const promise = (async () => {
        try {
          const pfFile = await getPfFile({
            axios: this.$axios,
            pfFileId,
          })

          if (pfFile) {
            commit('ADD_PF_FILE', pfFile)
          }
        } catch (err) {
          // 403 is an expected outcome: the user may see a reference to a file (e.g. an activity of a
          // restricted document) without being allowed to read the file itself. PfFileName renders
          // its not-found state for it, so there is nothing to report.
          if (err.response?.status !== 403) {
            console.error(err)
          }
        } finally {
          commit('REMOVE_FETCHING_ID', pfFileId)
          delete fetchByIdPromises[pfFileId]
        }
      })()

      fetchByIdPromises[pfFileId] = promise

      return promise
    },
  },
}
